/**
 * Regles de mot de passe des sites (« password rules » d'Apple, celles du generateur de Safari et du trousseau iCloud) :
 * un site qui refuse les symboles, plafonne a 16 caracteres ou exige un chiffre recoit du bouton « Generer » de
 * l'editeur un mot de passe qu'il accepte, au lieu d'un refus a l'inscription puis d'un mot de passe retouche a la main.
 *
 * Purement local, aucune E/S reseau : la table est embarquee (`password-rules-data.ts`, generee depuis
 * apple/password-manager-resources), le site de l'entree ne quitte jamais l'appareil.
 *
 * La lecture d'une regle est un PORTAGE de `tools/PasswordRulesParser/PasswordRulesParser.js` d'Apple (licence MIT,
 * Copyright (c) Apple Inc.) : memes fautes tolerees, memes fautes qui annulent la regle, memes cumuls (plus grande
 * longueur minimale, plus petite longueur maximale, plus petite repetition permise, classes exigees aussi permises).
 * Les classes sont ramenees a leurs caracteres ASCII imprimables (`unicode` compris : on ne tire jamais hors ASCII).
 *
 * Le tirage passe par le CSPRNG du coeur (`randomInt`, sans biais modulo) : chaque caractere est tire uniformement
 * parmi les caracteres permis, et un tirage qui viole une regle (classe exigee absente, repetition trop longue) est
 * REJETE en entier, jamais retouche -- le resultat est uniforme sur les mots de passe que le site accepte.
 */
import { randomInt } from '@klavox/crypto-core';
import { registrableDomain, siteHostname } from '@klavox/vault-model';
import { PASSWORD_RULES } from './password-rules-data.js';

/** Regle resolue d'un site : chaque classe est la chaine de ses caracteres, dans l'ordre ASCII. */
export interface SitePasswordRules {
  /** Une classe par `required:` : le mot de passe porte au moins un de ses caracteres. */
  readonly required: readonly string[];
  /** Caracteres permis (classes exigees comprises). */
  readonly allowed: string;
  readonly minLength: number | null;
  readonly maxLength: number | null;
  /** Plus longue suite permise d'un meme caractere (`max-consecutive`). */
  readonly maxConsecutive: number | null;
  /** `max-repeating` / `max-sequential` : sens non tire au clair, la regle n'est alors pas appliquee. */
  readonly unsupported: boolean;
}

const ASCII_PRINTABLE = Array.from({ length: 0x7e - 0x20 + 1 }, (_, i) => String.fromCharCode(0x20 + i)).join('');

function range(first: string, last: string): string {
  return ASCII_PRINTABLE.slice(first.charCodeAt(0) - 0x20, last.charCodeAt(0) - 0x20 + 1);
}

/** Classes nommees : `special` = ponctuation ASCII ET espace, comme chez Apple. */
const NAMED_CLASSES: Readonly<Record<string, string>> = {
  upper: range('A', 'Z'),
  lower: range('a', 'z'),
  digit: range('0', '9'),
  special: range(' ', '/') + range(':', '@') + range('[', '`') + range('{', '~'),
  'ascii-printable': ASCII_PRINTABLE,
  unicode: ASCII_PRINTABLE,
};

const RULE_NAMES = new Set(['allowed', 'required', 'max-consecutive', 'max-repeating', 'max-sequential', 'minlength', 'maxlength']);

const isIdentifierCharacter = (c: string | undefined): boolean => c !== undefined && /^[a-zA-Z-]$/u.test(c);
const isAsciiDigit = (c: string | undefined): boolean => c !== undefined && c >= '0' && c <= '9';
const isAsciiPrintable = (c: string): boolean => c >= ' ' && c <= '~';
const isAsciiWhitespace = (c: string | undefined): boolean => c === ' ' || c === '\f' || c === '\n' || c === '\r' || c === '\t';

function skipWhitespace(input: string, position: number): number {
  while (position < input.length && isAsciiWhitespace(input[position])) position++;
  return position;
}

function parseIdentifier(input: string, position: number): [string, number] {
  const start = position;
  while (position < input.length && isIdentifierCharacter(input[position])) position++;
  return [input.slice(start, position), position];
}

/** `[...]` : `-` litteral seulement en tete, `]]` en fin pour un `]` litteral, hors ASCII imprimable ignore. */
function parseCustomClass(input: string, position: number): [string[] | null, number] {
  const length = input.length;
  ++position;
  if (position >= length) return [null, position];
  const initial = position;
  const result: string[] = [];
  do {
    const c = input[position]!;
    if (!isAsciiPrintable(c)) {
      ++position;
      continue;
    }
    if (c === '-' && position - initial > 0) {
      ++position;
      continue;
    }
    result.push(c);
    ++position;
    if (c === ']') break;
  } while (position < length);
  if ((position < length && input[position] !== ']') || (position === length && input[position - 1] === ']')) {
    // Le `]` lu fermait la classe.
    result.pop();
    return [result, position];
  }
  if (position < length && input[position] === ']') return [result, position + 1];
  return [null, position];
}

function parseClassList(input: string, position: number): [string[] | null, number] {
  const length = input.length;
  const values: string[] = [];
  for (;;) {
    if (isIdentifierCharacter(input[position])) {
      const start = position;
      const [identifier, next] = parseIdentifier(input, position);
      const named = NAMED_CLASSES[identifier.toLowerCase()];
      if (named === undefined) return [null, start];
      values.push(named);
      position = next;
    } else if (input[position] === '[') {
      const [characters, next] = parseCustomClass(input, position);
      if (characters !== null && characters.length > 0) values.push(characters.join(''));
      position = next;
    } else {
      return [null, position];
    }
    position = skipWhitespace(input, position);
    if (position >= length || input[position] === ';') break;
    if (input[position] === ',') {
      position = skipWhitespace(input, position + 1);
      if (position >= length) return [null, position];
      continue;
    }
    return [null, position];
  }
  return [values, position];
}

function parseInteger(input: string, position: number): [number | null, number] {
  if (!isAsciiDigit(input[position])) return [null, position];
  let result = 0;
  do {
    result = 10 * result + Number(input[position]);
    ++position;
  } while (position < input.length && isAsciiDigit(input[position]));
  if (position >= input.length || input[position] === ';') return [result, position];
  return [null, position];
}

type ParsedRule = { readonly name: string; readonly value: string[] | number | null };

function parseRule(input: string, position: number): [ParsedRule | null, number] {
  const start = position;
  const [name, afterName] = parseIdentifier(input, position);
  if (!RULE_NAMES.has(name)) return [null, start];
  position = afterName;
  if (position >= input.length || input[position] !== ':') return [null, position];
  position = skipWhitespace(input, position + 1);
  if (position >= input.length || input[position] === ';') return [{ name, value: null }, position];
  if (name === 'allowed' || name === 'required') {
    const [values, next] = parseClassList(input, position);
    return [{ name, value: values }, next];
  }
  const [value, next] = parseInteger(input, position);
  // Comme Apple : 0 ne vaut pas regle.
  return [{ name, value: value === 0 ? null : value }, next];
}

/** `null` = regle illisible en entier (Apple la remplace alors par « tout ASCII imprimable », c.-a-d. aucune regle). */
function parseRulesInternal(input: string): ParsedRule[] | null {
  const rules: ParsedRule[] = [];
  const length = input.length;
  let position = skipWhitespace(input, 0);
  while (position < length) {
    if (!isIdentifierCharacter(input[position])) return rules;
    const [rule, next] = parseRule(input, position);
    if (rule !== null && rule.value !== null && !(Array.isArray(rule.value) && rule.value.length === 0)) rules.push(rule);
    position = skipWhitespace(input, next);
    if (position >= length) break;
    if (input[position] === ';') {
      position = skipWhitespace(input, position + 1);
      if (position >= length) return rules;
      continue;
    }
    return null;
  }
  return rules;
}

/** Union de classes, dans l'ordre ASCII. */
function unionOf(classes: readonly string[]): string {
  const seen = new Set(classes.join(''));
  return [...ASCII_PRINTABLE].filter((c) => seen.has(c)).join('');
}

/**
 * Lit une regle ecrite dans la langue d'Apple (`minlength: 8; maxlength: 16; required: lower, upper; required: digit;
 * allowed: [-_.];`). `null` si elle ne contraint rien (vide, ou illisible en entier comme chez Apple).
 */
export function parseSitePasswordRules(input: string): SitePasswordRules | null {
  const rules = parseRulesInternal(input) ?? [];
  const required: string[] = [];
  const allowed: string[] = [];
  let minLength = 0;
  let maxLength: number | null = null;
  let maxConsecutive: number | null = null;
  let unsupported = false;
  for (const rule of rules) {
    if (rule.name === 'required' && Array.isArray(rule.value)) {
      const union = unionOf(rule.value);
      required.push(union);
      allowed.push(union);
    } else if (rule.name === 'allowed' && Array.isArray(rule.value)) {
      allowed.push(unionOf(rule.value));
    } else if (typeof rule.value === 'number') {
      if (rule.name === 'minlength') minLength = Math.max(minLength, rule.value);
      else if (rule.name === 'maxlength') maxLength = maxLength === null ? rule.value : Math.min(maxLength, rule.value);
      else if (rule.name === 'max-consecutive') maxConsecutive = maxConsecutive === null ? rule.value : Math.min(maxConsecutive, rule.value);
      else unsupported = true;
    }
  }
  const resolved: SitePasswordRules = {
    required,
    allowed: allowed.length === 0 ? ASCII_PRINTABLE : unionOf(allowed),
    minLength: minLength > 0 ? minLength : null,
    maxLength,
    maxConsecutive,
    unsupported,
  };
  const constrains =
    resolved.required.length > 0 ||
    resolved.allowed !== ASCII_PRINTABLE ||
    resolved.minLength !== null ||
    resolved.maxLength !== null ||
    resolved.maxConsecutive !== null ||
    resolved.unsupported;
  return constrains ? resolved : null;
}

/** Index `domaine -> regle` depuis le format genere (`domaine<TAB>regle`, une ligne par domaine). */
export function indexSitePasswordRules(data: string): ReadonlyMap<string, string> {
  const index = new Map<string, string>();
  for (const line of data.split('\n')) {
    const tab = line.indexOf('\t');
    if (tab > 0) index.set(line.slice(0, tab), line.slice(tab + 1));
  }
  return index;
}

let defaultIndex: ReadonlyMap<string, string> | null = null;

/** L'index de la table embarquee, construit une seule fois. */
export function defaultSitePasswordRules(): ReadonlyMap<string, string> {
  return (defaultIndex ??= indexSitePasswordRules(PASSWORD_RULES));
}

export interface SiteRulesMatch {
  /** Le domaine de la table qui a repondu. */
  readonly domain: string;
  readonly rules: SitePasswordRules;
}

/**
 * Regle du site d'une adresse : l'hote exact, a defaut un de ses domaines parents jusqu'au domaine enregistrable
 * (`www.amazon.co.uk` -> `amazon.co.uk`, jamais `co.uk`). Seul le web compte (`siteHostname`) ; une regle illisible
 * ou dont une partie n'est pas comprise (`unsupported`) ne s'applique pas.
 */
export function sitePasswordRulesFor(uri: string, index: ReadonlyMap<string, string> = defaultSitePasswordRules()): SiteRulesMatch | null {
  const hostname = siteHostname(uri);
  if (hostname === null || hostname === '') return null;
  const floor = registrableDomain(hostname) ?? hostname;
  for (let host = hostname; host.length >= floor.length; host = host.slice(host.indexOf('.') + 1)) {
    const text = index.get(host);
    if (text !== undefined) {
      const rules = parseSitePasswordRules(text);
      return rules === null || rules.unsupported ? null : { domain: host, rules };
    }
    if (!host.includes('.')) break;
  }
  return null;
}

/** Longueur retenue : celle du generateur, ramenee dans les bornes du site. `null` si les bornes se contredisent. */
export function sitePasswordLength(rules: SitePasswordRules, preferred: number): number | null {
  const min = rules.minLength ?? 1;
  const max = rules.maxLength ?? Number.POSITIVE_INFINITY;
  if (min > max) return null;
  return Math.min(max, Math.max(min, preferred));
}

/** Le mot de passe respecte-t-il la regle ? (classes exigees, caracteres permis, longueur, repetitions) */
export function satisfiesSitePasswordRules(password: string, rules: SitePasswordRules): boolean {
  const chars = [...password];
  if (rules.minLength !== null && chars.length < rules.minLength) return false;
  if (rules.maxLength !== null && chars.length > rules.maxLength) return false;
  if (!chars.every((c) => rules.allowed.includes(c))) return false;
  if (!rules.required.every((set) => chars.some((c) => set.includes(c)))) return false;
  if (rules.maxConsecutive !== null) {
    let run = 0;
    for (let i = 0; i < chars.length; i++) {
      run = i > 0 && chars[i] === chars[i - 1] ? run + 1 : 1;
      if (run > rules.maxConsecutive) return false;
    }
  }
  return true;
}

/** Tirages au plus avant d'abandonner (aucune regle de la table n'en approche ; une regle forgee ne bloque pas l'IHM). */
export const SITE_PASSWORD_MAX_DRAWS = 1000;

/** Caracteres que le tirage utilise : ceux que le site permet, sauf l'espace (un espace de fin se perd au copier-coller). */
function drawnCharacters(rules: SitePasswordRules): string[] {
  return [...rules.allowed].filter((c) => c !== ' ');
}

/**
 * Tire un mot de passe que le site accepte, a la longueur du generateur ramenee dans ses bornes. L'espace, permis par
 * la classe `special` d'Apple, n'est jamais tire (un espace de fin se perd au copier-coller). `null` si la regle ne
 * laisse aucun mot de passe possible.
 */
export function generateSitePassword(rules: SitePasswordRules, preferredLength: number): string | null {
  const length = sitePasswordLength(rules, preferredLength);
  if (length === null || rules.required.length > length) return null;
  const pool = drawnCharacters(rules);
  if (pool.length === 0 || rules.required.some((set) => ![...set].some((c) => c !== ' ' && pool.includes(c)))) return null;
  for (let draw = 0; draw < SITE_PASSWORD_MAX_DRAWS; draw++) {
    const chars: string[] = [];
    for (let i = 0; i < length; i++) chars.push(pool[randomInt(pool.length)]!);
    const password = chars.join('');
    if (satisfiesSitePasswordRules(password, rules)) return password;
  }
  return null;
}

/**
 * Entropie d'un mot de passe de `generateSitePassword` : log2 du nombre de mots de passe de la longueur retenue, sur les
 * caracteres tires, qui portent chaque classe exigee (inclusion-exclusion : le tirage rejete est uniforme sur eux).
 * `max-consecutive` n'est pas decompte (estimation haute : seuls manquent les tirages a repetition trop longue).
 * 0 si la regle ne laisse rien de possible.
 */
export function sitePasswordEntropyBits(rules: SitePasswordRules, preferredLength: number): number {
  const length = sitePasswordLength(rules, preferredLength);
  const pool = drawnCharacters(rules);
  if (length === null || pool.length === 0 || rules.required.length > length) return 0;
  const required = rules.required.map((set) => [...set].filter((c) => pool.includes(c)));
  if (required.some((set) => set.length === 0)) return 0;
  // Part des tirages qui portent chaque classe exigee : somme sur les classes ecartees, signe selon leur nombre.
  let share = 0;
  for (let mask = 0; mask < 1 << required.length; mask++) {
    const excluded = new Set<string>();
    let count = 0;
    required.forEach((set, i) => {
      if ((mask & (1 << i)) === 0) return;
      count++;
      for (const c of set) excluded.add(c);
    });
    share += (count % 2 === 0 ? 1 : -1) * ((pool.length - excluded.size) / pool.length) ** length;
  }
  return share <= 0 ? 0 : length * Math.log2(pool.length) + Math.log2(share);
}

const CLASS_NAMES: readonly [string, string][] = [
  [NAMED_CLASSES.lower!, 'minuscule'],
  [NAMED_CLASSES.upper!, 'majuscule'],
  [NAMED_CLASSES.digit!, 'chiffre'],
];

/** Une classe en mots : « chiffre », « minuscule ou majuscule », « l’un de !@# ». */
function describeClass(set: string): string {
  const parts: string[] = [];
  let rest = set;
  for (const [chars, label] of CLASS_NAMES) {
    if ([...chars].every((c) => rest.includes(c))) {
      parts.push(label);
      rest = [...rest].filter((c) => !chars.includes(c)).join('');
    }
  }
  const symbols = rest.replace(/ /gu, '');
  if (symbols !== '') parts.push(symbols === NAMED_CLASSES.special!.replace(/ /gu, '') ? 'symbole' : `l’un de ${symbols}`);
  return parts.join(' ou ');
}

/** Resume lisible de la regle, pour l'indication sous le champ du mot de passe. */
export function describeSitePasswordRules(rules: SitePasswordRules): string {
  const parts: string[] = [];
  if (rules.minLength !== null && rules.maxLength !== null) parts.push(`${rules.minLength} a ${rules.maxLength} caracteres`);
  else if (rules.minLength !== null) parts.push(`${rules.minLength} caracteres au moins`);
  else if (rules.maxLength !== null) parts.push(`${rules.maxLength} caracteres au plus`);
  for (const set of rules.required) parts.push(`au moins un ${describeClass(set)}`);
  const symbols = [...rules.allowed].filter((c) => NAMED_CLASSES.special!.includes(c) && c !== ' ').join('');
  if (symbols === '') parts.push('aucun symbole');
  else if (symbols.length < NAMED_CLASSES.special!.length - 1) parts.push(`symboles permis : ${symbols}`);
  if (rules.maxConsecutive !== null) parts.push(`pas plus de ${rules.maxConsecutive} caracteres identiques a la suite`);
  return parts.join(' ; ');
}
